import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { switchMap, of, tap, catchError } from "rxjs";
import FullNameNotPublic from "../social/FullNameNotPublic";
import PublicationScreen from "./PublicationScreen";
import VariantScreen from "./VariantScreen";
import Btn from "../atoms/Btn";
import Loader from "../Loader";
import PubmedEntry from "./PubmedEntry";
import SourceEntity from "../Publications/models/SourceEntity";
import PhenotypeScreen from "./PhenotypeScreen";
import CommentScreen from "./CommentScreen";
import AddToast from "../../state/actions/AddToast";
import apiClient from "../../services/apiClient";
import isUserProfilePublic from "../../common/helpers/isUserProfilePublic";
import {
  getUserFullName,
  getUserOrganization,
} from "../../state/selectors/general.selectors";
import { getSearchEntry } from "../../state/selectors/Publications.selectors";
import Search from "../../state/actions/Search";
import Events from "../../common/helpers/event-helpers/events";
import store from "../../state/store";

function PubmedFormWidget({
  entry,
  sourceEntity,
  allowPublicationUpdate,
  onCancel,
  searchEntry,
  userFullName,
  userOrganisation,
}) {
  const isUserPublic = useMemo(() => isUserProfilePublic(), []);
  const [isLoading, setIsLoading] = useState(false);
  const [pubmedEntry, setPubmedEntry] = useState(entry || searchEntry);
  const { groupedVariants, pubmedId } = pubmedEntry;

  const hasVariants = groupedVariants?.length || 0;
  const hasPathClass = groupedVariants.filter(
    (x) => x?.pathogenicity?.value
  ).length;

  const canSubmit = hasPathClass && pubmedId;
  const noVarMsg = "Please select at least one variant";
  const noPubmedMsg = "Please enter a valid PubMed ID";
  const noVarPathogenicity = "Please select pathogenicity for all variants";
  const varMsg = hasVariants ? "" : noVarMsg;
  const pathMsg = hasPathClass ? "" : noVarPathogenicity;
  const pmidMsg = pubmedId ? "" : noPubmedMsg;
  const msg = [varMsg, pathMsg, pmidMsg].filter(Boolean).join("<br/>");

  function onSuccess() {
    const state = store.getState();
    if (state && state.variantResult) {
      setTimeout(() => Search(0, true), 150);
    } else {
      Events.dispatch("PUBMED_CHANGED");
    }
  }

  function save() {
    const { id } = pubmedEntry;
    const data = pubmedEntry.normalizeForApi();

    of(null)
      .pipe(
        tap(() => setIsLoading(true)),
        switchMap(() => apiClient.updateClassification(id, data)),
        switchMap(() => (id ? apiClient.getClassification(id) : of(null))),
        tap(() => AddToast("saved successfully", "success")),
        tap((res) => onSuccess(res)),
        catchError((err) => {
          console.error(err);
          AddToast("Something went wrong", "error");
          return of(null);
        }),
        tap(() => setIsLoading(false))
      )
      .subscribe();
  }

  function deleteEntry() {
    of(null)
      .pipe(
        tap(() => setIsLoading(true)),
        switchMap(() => apiClient.deleteClassification(pubmedEntry.id)),
        tap(() => AddToast("deleted successfully", "success")),
        tap(() => onSuccess(pubmedEntry)),
        catchError(() => {
          AddToast("Something went wrong", "error");
          return of(null);
        }),
        tap(() => setIsLoading(false))
      )
      .subscribe();
  }

  if (isLoading) {
    return <Loader />;
  }

  if (!isUserPublic) {
    return <FullNameNotPublic />;
  }

  return (
    <div>
      <div>
        Here you classify variants based on information within the linked
        publication. <br />
        Linking publications to variants is an important contribution you can
        make to the
        <b> worldwide genetics</b>. It is important that you enter accurate
        information and that you link this publication to the correct
        variant(s). We check all entries, and we will remove incorrect ones. If
        we detect abuse, we may suspend or remove your user account.
      </div>

      <div className="tw-h-12" />

      <PublicationScreen
        allowPublicationUpdate={allowPublicationUpdate}
        entry={pubmedEntry}
        onEntryUpdated={(x) => setPubmedEntry(x)}
      />

      <div className="tw-h-12" />

      <VariantScreen
        entry={pubmedEntry}
        onEntryUpdated={(x) => setPubmedEntry(x)}
      />

      <div className="tw-h-12" />

      <PhenotypeScreen
        sourceEntity={sourceEntity}
        entry={pubmedEntry}
        onEntryUpdated={(x) => setPubmedEntry(x)}
      />
      <div className="tw-h-12" />

      <CommentScreen
        entry={pubmedEntry}
        onEntryUpdated={(x) => setPubmedEntry(x)}
      />

      <div className="tw-text-blueGray-400">
        {`Your contribution will be displayed using your real name: ${userFullName} ${
          userOrganisation ? `${`[${userOrganisation}`}]` : ""
        }`}
      </div>

      <div className="tw-h-12" />

      <div className="tw-flex tw-items-center tw-gap-6">
        <div className="tw-flex-grow" />

        <div>
          <Btn
            className="tw-text-primary tw-px-2"
            onSubmit={onCancel}
            id="cancel-button"
          >
            Cancel
          </Btn>
        </div>

        {pubmedEntry.id ? (
          <div>
            <Btn
              id="link-var-remove"
              className="btn btn-block btn-danger tw-border"
              onSubmit={() => deleteEntry(pubmedEntry)}
            >
              Delete
            </Btn>
          </div>
        ) : null}

        <div data-tooltip={msg} data-tooltip-dangerous-html="true">
          <Btn
            className={`btn btn-block btn-success tw-border tw-border-primary ${
              canSubmit
                ? ""
                : "tw-opacity-50 tw-cursor-not-allowed tw-pointer-events-none"
            }`}
            onSubmit={() => save(pubmedEntry)}
            disabled={!canSubmit}
          >
            Finish
            <i className="varsome-icons icon-chevron-right tw-ml-2 tw-text-xs" />
          </Btn>
        </div>
      </div>
    </div>
  );
}

PubmedFormWidget.propTypes = {
  entry: PropTypes.instanceOf(PubmedEntry),
  searchEntry: PropTypes.instanceOf(PubmedEntry),
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  allowPublicationUpdate: PropTypes.bool,
  onCancel: PropTypes.func,
  userFullName: PropTypes.string,
  userOrganisation: PropTypes.string,
};

PubmedFormWidget.defaultProps = {
  entry: null,
  searchEntry: null,
  sourceEntity: null,
  allowPublicationUpdate: true,
  userFullName: "",
  userOrganisation: "",
  onCancel: () => {},
};

const mapStateToProps = (state) => {
  return {
    userFullName: getUserFullName(state),
    userOrganisation: getUserOrganization(state),
    searchEntry: getSearchEntry(state),
  };
};

export default connect(mapStateToProps, {})(PubmedFormWidget);
